/**
 * Frontend observability — Sentry init + correlation helpers.
 *
 * Mirrors the discipline of the backend `src/lib/sentry.ts` module:
 *   - `sendDefaultPii: false` (never ship email/name; server-side IP storage
 *     is disabled separately in each Sentry project)
 *   - No-op when DSN is empty (local dev without a Sentry project)
 *   - Scope-bounded `captureException` for actionable api-client failures
 *
 * Sampling profile (see docs/observability.md §5.1):
 *   - `tracesSampleRate`: 1.0 dev / 0.2 prod
 *   - `profilesSampleRate`: 1.0 dev / 0.2 prod
 *   - `replaysSessionSampleRate`: 0.1 for dashboard + agent / 0 for backoffice
 *     (super-admin screens are high-privilege — never auto-record sessions)
 *   - `replaysOnErrorSampleRate`: 1.0 for all apps
 *
 * Exposed surface:
 *   - `initSentry()` — call once, before React renders.
 *   - `setAuthContext(ctx | null)` — bind identity (sub + tags only; no PII).
 *   - `onApiError(err, { requestId, traceId })` — capture with correlation tags.
 *   - `addApiBreadcrumb({ method, path, requestId, traceId, status })` — trail.
 */

import type { ApiError } from '@cloneai/api-client';
import * as Sentry from '@sentry/react';

export type AppName = 'dashboard' | 'backoffice' | 'agent';
export type Environment = 'dev' | 'prod';

export interface InitSentryOptions {
  /** Which SPA this is — picks the replay privacy and sample-rate profile. */
  app: AppName;
  /** Deployed environment. Forwarded to Sentry + used to pick sample rates. */
  environment: Environment;
  /** Release identifier (git sha in CI, `'local'` in dev). */
  release: string;
  /**
   * Project DSN. When empty (local dev without a Sentry project) init is a
   * no-op so the surrounding capture* calls become inert.
   */
  dsn: string;
}

export interface AuthContext {
  userSub: string;
  tenantId: string | null;
  userGroup: string | null;
}

/** Tag keys we manage — tracked so `setAuthContext(null)` can clear them. */
const AUTH_TAG_KEYS = ['tenantId', 'userGroup'] as const;

/**
 * Initialise Sentry for a single SPA. Must be called once, as early as
 * possible — before `createRoot(...).render(...)`. If `dsn` is empty, this
 * is a no-op; subsequent helpers still safely forward to Sentry (which
 * itself no-ops when uninitialised).
 */
export function initSentry(opts: InitSentryOptions): void {
  if (!opts.dsn) {
    return;
  }

  const isDev = opts.environment === 'dev';
  const tracesSampleRate = isDev ? 1.0 : 0.2;
  const profilesSampleRate = isDev ? 1.0 : 0.2;
  const replaysSessionSampleRate = opts.app === 'backoffice' ? 0 : isDev ? 1.0 : 0.1;
  const replaysOnErrorSampleRate = 1.0;

  Sentry.init({
    dsn: opts.dsn,
    environment: opts.environment,
    release: opts.release,
    // Never send email/name. Sentry's project-level "Prevent Storing of IP
    // Addresses" control must also remain enabled; SDK configuration cannot
    // prevent the ingestion service from observing the transport IP.
    sendDefaultPii: false,
    tracesSampleRate,
    profilesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    // Enable Sentry Logs (v8 preview). Forwards Sentry.logger.* calls to the
    // Logs product for structured frontend logging alongside events.
    _experiments: {
      enableLogs: true,
    },
    integrations: [
      // Performance: fetch/XHR spans + route-change transactions.
      Sentry.browserTracingIntegration(),
      // Profiling: CPU samples during traced transactions (requires
      // `profilesSampleRate` > 0, already set above).
      Sentry.browserProfilingIntegration(),
      // Session Replay: records DOM mutations + network for visual debugging.
      // Per docs/observability.md §5.1: session rate 0.1 dashboard/agent
      // in prod, 1.0 in dev, 0 backoffice; on-error 1.0 all apps.
      Sentry.replayIntegration({
        // Backoffice screens are super-admin; always mask text content.
        // Dashboard + public agent text remains visible so replay is useful;
        // inputs are still always masked.
        maskAllText: opts.app === 'backoffice',
        maskAllInputs: true,
        // Agent runtime debugging needs to see public videos. Dashboard and
        // backoffice keep media blocked because those surfaces can contain
        // tenant/private UI.
        blockAllMedia: opts.app !== 'agent',
      }),
      // Surrounding source lines on stack frames (when source maps are uploaded).
      Sentry.contextLinesIntegration(),
      // API error events are captured explicitly by @cloneai/api-client after
      // retries are exhausted. Its breadcrumbs retain handled 4xx context
      // without the SDK's http integration creating a duplicate issue.
      // Capture console.error calls as events. `levels: ['error']` keeps
      // signal-to-noise sane; we don't want console.log spam in Sentry.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
      // User-facing feedback widget. Users can report bugs; we auto-attach
      // the current Sentry scope (requestId + traceId + tenantId tags). No
      // screenshots (keeps PII out of reports; can opt in per-tenant later).
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        showBranding: false,
        showEmail: false,
        showName: false,
        autoInject: false, // caller mounts <ShowFeedbackButton /> where appropriate
      }),
    ],
  });

  Sentry.setTag('app', opts.app);
}

/**
 * Structured logging forwarder. Calls land in Sentry Logs (v8 preview).
 * Use sparingly — prefer `captureException` for real failures and
 * `addApiBreadcrumb` for normal trail. Typical use: a high-signal client
 * event you want indexed alongside backend logs (e.g. "tenant switcher
 * opened", "feature-flag X evaluated").
 *
 * Calls are no-ops when `_experiments.enableLogs` isn't set (local dev
 * without a DSN).
 *
 * `Sentry.logger` is a runtime-present experimental API; @sentry/react
 * v8.55's type definitions don't export it yet. We access it via an
 * `unknown`-typed bridge so the surface stays well-typed at the call
 * site but the compile doesn't choke on the missing type export.
 */
interface SentryLogger {
  debug(message: string, attrs?: Record<string, unknown>): void;
  info(message: string, attrs?: Record<string, unknown>): void;
  warn(message: string, attrs?: Record<string, unknown>): void;
  error(message: string, attrs?: Record<string, unknown>): void;
}

function getSentryLogger(): SentryLogger | null {
  const sentryNamespace = Sentry as unknown as Record<string, unknown>;
  return (sentryNamespace['logger'] as SentryLogger | undefined) ?? null;
}

export const logger = {
  debug: (message: string, attrs?: Record<string, unknown>): void => {
    getSentryLogger()?.debug(message, attrs);
  },
  info: (message: string, attrs?: Record<string, unknown>): void => {
    getSentryLogger()?.info(message, attrs);
  },
  warn: (message: string, attrs?: Record<string, unknown>): void => {
    getSentryLogger()?.warn(message, attrs);
  },
  error: (message: string, attrs?: Record<string, unknown>): void => {
    getSentryLogger()?.error(message, attrs);
  },
};

export interface MetricOptions {
  readonly tags?: Record<string, string | number | boolean | null | undefined>;
  readonly unit?: string;
}

interface SentryMetricOptions {
  tags?: Record<string, string>;
  unit?: string;
}

interface SentryMetrics {
  increment?(name: string, value?: number, options?: SentryMetricOptions): void;
  distribution?(name: string, value: number, options?: SentryMetricOptions): void;
  gauge?(name: string, value: number, options?: SentryMetricOptions): void;
}

function getSentryMetrics(): SentryMetrics | null {
  const sentryNamespace = Sentry as unknown as Record<string, unknown>;
  return (sentryNamespace['metrics'] as SentryMetrics | undefined) ?? null;
}

function toMetricOptions(opts?: MetricOptions): SentryMetricOptions | undefined {
  if (!opts) return undefined;

  const normalized: SentryMetricOptions = {};
  if (opts.unit) {
    normalized.unit = opts.unit;
  }
  if (opts.tags) {
    const tags: Record<string, string> = {};
    for (const [key, value] of Object.entries(opts.tags)) {
      if (value !== null && value !== undefined) {
        tags[key] = String(value);
      }
    }
    if (Object.keys(tags).length > 0) {
      normalized.tags = tags;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

export const metrics = {
  increment: (name: string, value = 1, opts?: MetricOptions): void => {
    getSentryMetrics()?.increment?.(name, value, toMetricOptions(opts));
  },
  distribution: (name: string, value: number, opts?: MetricOptions): void => {
    getSentryMetrics()?.distribution?.(name, value, toMetricOptions(opts));
  },
  gauge: (name: string, value: number, opts?: MetricOptions): void => {
    getSentryMetrics()?.gauge?.(name, value, toMetricOptions(opts));
  },
  timing: (name: string, durationMs: number, opts?: Omit<MetricOptions, 'unit'>): void => {
    getSentryMetrics()?.distribution?.(
      name,
      durationMs,
      toMetricOptions({ ...opts, unit: 'millisecond' }),
    );
  },
};

interface FeedbackForm {
  appendToDom: () => void;
  removeFromDom: () => void;
  open: () => void;
}

let activeFeedbackForm: FeedbackForm | null = null;

/**
 * Programmatically show the feedback dialog. Wire this to a "Report a bug"
 * button in the shell topbar (or anywhere the user might surface an error).
 * Returns void; if the feedback integration isn't registered (empty DSN)
 * this is a silent no-op.
 */
export function showFeedbackDialog(): void {
  const feedback = Sentry.getFeedback();
  if (!feedback) return;

  if (activeFeedbackForm) {
    activeFeedbackForm.open();
    return;
  }

  void feedback
    .createForm({
      onFormClose: () => {
        activeFeedbackForm?.removeFromDom();
        activeFeedbackForm = null;
      },
      onFormSubmitted: () => {
        activeFeedbackForm?.removeFromDom();
        activeFeedbackForm = null;
      },
    })
    .then((form) => {
      activeFeedbackForm = form;
      form.appendToDom();
      form.open();
    })
    .catch(() => {
      activeFeedbackForm = null;
    });
}

/**
 * Bind the current identity to Sentry scope. Called from each app's
 * `AuthProvider` whenever the session user resolves (or clears).
 *
 * When `ctx` is null the user is cleared and the tags we own are wiped so
 * stray events after sign-out can't be mis-attributed.
 *
 * Never pass email, name, or any other PII through here.
 */
export function setAuthContext(ctx: AuthContext | null): void {
  if (ctx === null) {
    Sentry.setUser(null);
    for (const key of AUTH_TAG_KEYS) {
      Sentry.setTag(key, undefined);
    }
    return;
  }

  Sentry.setUser({ id: ctx.userSub });
  Sentry.setTags({
    tenantId: ctx.tenantId ?? 'none',
    userGroup: ctx.userGroup ?? 'none',
  });
}

export interface ApiErrorContext {
  requestId: string;
  traceId: string | null;
}

/**
 * Capture an `ApiError` with full correlation tags (requestId + X-Ray
 * traceId) so Logs Insights → ServiceLens → Sentry round-trips in a few
 * clicks.
 */
export function onApiError(err: ApiError, ctx: ApiErrorContext): void {
  Sentry.withScope((scope) => {
    scope.setTags({
      requestId: ctx.requestId,
      traceId: ctx.traceId ?? 'none',
      errorCode: err.errorCode ?? 'none',
      status: String(err.status),
    });
    if (err.detail) {
      scope.setExtra('detail', err.detail);
    }
    Sentry.captureException(err);
  });
}

export interface ApiBreadcrumbInput {
  method: string;
  path: string;
  requestId: string;
  traceId: string | null;
  status: number;
  ok: boolean;
}

/**
 * Drop an `api` breadcrumb for every request (success or failure).
 * Success entries are `info`; failures are `error`. Every breadcrumb carries
 * `requestId` + `traceId` so a single captured exception includes the full
 * HTTP trail.
 */
export function addApiBreadcrumb(input: ApiBreadcrumbInput): void {
  Sentry.addBreadcrumb({
    category: 'api',
    message: `${input.method} ${input.path}`,
    data: {
      requestId: input.requestId,
      traceId: input.traceId,
      status: input.status,
    },
    level: input.ok ? 'info' : 'error',
  });
}
